
import React from "react";
import { Globe, Upload, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useValidation } from "@/contexts/ValidationContext";

interface OnlinePresenceStepProps {
  businessData: {
    website: string;
    keywords: string[];
    colors: string[];
  };
  updateBusinessData: (key: string, value: string | string[]) => void;
  analyzeWebsite: () => Promise<void>;
  isAnalyzing: boolean;
  analysisError: string | null;
}

const OnlinePresenceStep: React.FC<OnlinePresenceStepProps> = ({ 
  businessData, 
  updateBusinessData,
  analyzeWebsite,
  isAnalyzing,
  analysisError
}) => {
  const { getFieldError } = useValidation();
  const websiteError = getFieldError('website');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="text-primary h-6 w-6" />
        <h3 className="text-2xl font-medium">Online Presence</h3>
      </div>
      
      <Separator />

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="website">Business Website <span className="text-red-500">*</span></Label>
          <div className="flex gap-2">
            <Input
              id="website"
              placeholder="https://yourbusiness.com"
              value={businessData.website}
              onChange={(e) => updateBusinessData("website", e.target.value)}
              className={`flex-1 ${websiteError ? "border-red-500" : ""}`}
            />
            <Button 
              variant="secondary" 
              onClick={analyzeWebsite}
              disabled={isAnalyzing || !businessData.website}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Analyze
                </>
              )}
            </Button>
          </div>
          {websiteError && (
            <p className="text-red-500 text-sm mt-1">{websiteError}</p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            We'll analyze your website to understand your brand style and content.
          </p>
        </div>
        
        {analysisError && (
          <Alert variant="destructive" className="mt-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{analysisError}</AlertDescription>
          </Alert>
        )}
        
        {businessData.keywords.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Detected Keywords</h4>
            <div className="flex flex-wrap gap-2">
              {businessData.keywords.map(keyword => (
                <Badge key={keyword} variant="secondary" className="text-xs">
                  {keyword}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {businessData.colors.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Brand Colors</h4>
            <div className="flex gap-2">
              {businessData.colors.map(color => (
                <div 
                  key={color} 
                  className="h-8 w-8 rounded-full border"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}
        
        <div className="mt-6">
          <h4 className="text-lg font-medium mb-2">Upload Brand Assets (Optional)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:bg-muted/10 transition-colors cursor-pointer">
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium">Logo</p>
              <p className="text-xs text-muted-foreground mt-1">PNG, JPG or SVG</p>
            </div>
            
            <div className="border-2 border-dashed border-muted rounded-lg p-6 text-center hover:bg-muted/10 transition-colors cursor-pointer">
              <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium">Brand Guidelines</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, DOCX</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnlinePresenceStep;
